import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { getVersions, getComparison, prepareComparison, analyzeComparison } from '../features/bills/billSlice';
import Dropdown from '../components/Structures/Dropdown';
import ThreeArrowsSVG from '../components/SVGs/ThreeArrowsSVG';
import MainLoader from '../components/Structures/MainLoader';
import { IoMdArrowBack } from 'react-icons/io';

const splitCompareEnabled = process.env.REACT_APP_PERF_COMPARE_SPLIT_AI === '1';

const DIFF_OP_CLASS = { equal: '', insert: 'diff-insert', delete: 'diff-delete' };

const BADGE = {
  modified: { cls: 'diff-badge-modified', label: 'Modified' },
  added: { cls: 'diff-badge-added', label: 'Added' },
  removed: { cls: 'diff-badge-removed', label: 'Removed' },
  unchanged: { cls: 'diff-badge-unchanged', label: 'Unchanged' },
};

const toPlain = (text) =>
  String(text || '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const firstMeaningfulLine = (text, max = 90) => {
  const plain = toPlain(text);
  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
};

// Concatenate diff ops into a single HTML string, each op wrapped in a span.
// Ops are joined *before* sanitizing/parsing because a bill's own <u>/<s> tag
// can open in one op and close in the next — splitting per op would break it.
const opsToHtml = (ops, { showInsert = true, showDelete = true } = {}) =>
  (ops || [])
    .filter((op) => (op.op === 'insert' ? showInsert : op.op === 'delete' ? showDelete : true))
    .map((op) => {
      const cls = DIFF_OP_CLASS[op.op] || '';
      return `<span${cls ? ` class="${cls}"` : ''}>${op.text}</span>`;
    })
    .join('');

// Render a legislative HTML string as real React nodes. The string is first
// run through DOMPurify, then parsed with DOMParser and walked into elements —
// so no raw innerHTML is ever injected, and only the sanitized subset of tags
// (span/u/b/i/s/strike/em/strong, plus their classes) survives.
const htmlToReact = (html) => {
  const clean = DOMPurify.sanitize(String(html || ''));
  const body = new DOMParser().parseFromString(clean, 'text/html').body;

  const toNode = (node, key) => {
    if (node.nodeType === 3) return node.textContent; // text node
    if (node.nodeType !== 1) return null; // ignore comments etc.
    const tag = node.nodeName.toLowerCase();
    const props = { key };
    const cls = node.getAttribute && node.getAttribute('class');
    if (cls) props.className = cls;
    const children = Array.from(node.childNodes).map((child, i) => toNode(child, `${key}-${i}`));
    return React.createElement(tag, props, children.length ? children : undefined);
  };

  return Array.from(body.childNodes).map((child, i) => toNode(child, `n-${i}`));
};

const DiffHtml = ({ html }) => <div className="bill-diff">{htmlToReact(html)}</div>;

const Compare = () => {
  const { billId, state } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { versions, comparison, isLoading: billLoading } = useSelector((s) => s.bill);
  const { session } = useSelector((s) => s.user);

  const [selectedVersion1, setSelectedVersion1] = useState('');
  const [selectedVersion2, setSelectedVersion2] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [sideBySide, setSideBySide] = useState(false);
  const [onlyChanges, setOnlyChanges] = useState(false);
  const [rowOverrides, setRowOverrides] = useState({}); // idx -> bool (open)
  const [sidebarTab, setSidebarTab] = useState('impact'); // 'impact' | 'changes'

  useEffect(() => {
    if (billId) {
      dispatch(getVersions({ billId, selectedSession: session }));
    }
  }, [billId, dispatch, session]);

  useEffect(() => {
    setSelectedVersion1(versions.includes('FIL') ? 'FIL' : '');
  }, [versions]);

  const rows = useMemo(() => comparison?.preparedData?.rows || [], [comparison]);

  // Reset per-row collapse state whenever a fresh comparison arrives.
  useEffect(() => {
    setRowOverrides({});
  }, [rows]);

  const counts = useMemo(() => {
    const c = { modified: 0, added: 0, removed: 0, unchanged: 0 };
    rows.forEach((r) => {
      c[r.changeType] = (c[r.changeType] || 0) + 1;
    });
    return c;
  }, [rows]);

  const changedCount = counts.modified + counts.added + counts.removed;

  const isRowOpen = (row, idx) => (idx in rowOverrides ? rowOverrides[idx] : row.changeType !== 'unchanged');
  const toggleRow = (row, idx) => setRowOverrides((prev) => ({ ...prev, [idx]: !isRowOpen(row, idx) }));
  const setAllRows = (open) => {
    const next = {};
    rows.forEach((_, idx) => {
      next[idx] = open;
    });
    setRowOverrides(next);
  };

  // Open the target section (if collapsed) and scroll it into view. The row's
  // wrapper is always in the DOM regardless of open/collapse state, so the
  // scroll target resolves even when it's currently collapsed.
  const jumpToRow = (idx) => {
    setRowOverrides((prev) => ({ ...prev, [idx]: true }));
    const el = document.getElementById(`diff-row-${idx}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCompare = async () => {
    if (!selectedVersion1 || !selectedVersion2) return;
    setIsLoading(true);
    try {
      if (splitCompareEnabled) {
        const prepared = await dispatch(
          prepareComparison({ billId, session, versions: [selectedVersion1, selectedVersion2] })
        ).unwrap();
        await dispatch(analyzeComparison({ analysisCacheKey: prepared.analysisCacheKey })).unwrap();
      } else {
        await dispatch(getComparison({ billId, session, versions: [selectedVersion1, selectedVersion2] })).unwrap();
      }
    } catch (error) {
      console.error('Error during comparison:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const aiResponse = comparison?.aiResponse;
  const hasComparison = rows.length > 0;
  const visibleRows = rows
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => !onlyChanges || row.changeType !== 'unchanged');

  // The changed sections only — drives the right-hand jump navigator.
  const changedRows = rows
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => row.changeType !== 'unchanged');

  const navLabel = (row) =>
    firstMeaningfulLine(
      row.changeType === 'added' ? (row.diff || []).map((d) => d.text).join('') : row.left,
      44
    );

  const renderRowBody = (row) => {
    if (sideBySide) {
      return (
        <div className="diff-sbs">
          <div className="diff-sbs-col">
            <h4>{selectedVersion1} (original)</h4>
            {row.left != null ? (
              <DiffHtml html={String(row.left)} />
            ) : (
              <p className="text-gray-400 italic">— not present —</p>
            )}
          </div>
          <div className="diff-sbs-col">
            <h4>{selectedVersion2} (updated)</h4>
            {row.diff ? (
              <DiffHtml html={opsToHtml(row.diff, { showDelete: false })} />
            ) : (
              <p className="text-gray-400 italic">— removed —</p>
            )}
          </div>
        </div>
      );
    }

    // Inline unified diff.
    if (row.changeType === 'removed') {
      return <DiffHtml html={opsToHtml([{ op: 'delete', text: row.left }])} />;
    }
    return <DiffHtml html={opsToHtml(row.diff)} />;
  };

  if (isLoading || billLoading) return <MainLoader />;

  return (
    <>
      {/* ---- Version picker ---- */}
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] py-[15px] px-5 w-full mb-[20px]">
        <div className="border-b border-[#E5E7EB] mb-5 pb-2.5 flex items-center gap-8">
          <button
            className="hover:bg-darkblue-hover/15"
            onClick={() => navigate(`/${state}/bill/${billId}?session=${session}`)}
          >
            <IoMdArrowBack className="w-12 h-12 text-darkblue" />
          </button>
          <h2 className="font-playfair text-[28px] text-darkblue font-medium leading-8">Compare Versions</h2>
        </div>
        <div className="flex items-center gap-[15px]">
          <div className="hidden min-[1161px]:flex items-center gap-3 text-base font-medium text-black">
            Version
            <ThreeArrowsSVG />
          </div>
          <div className="flex flex-col lg:flex-row w-full items-center gap-[15px]">
            <Dropdown
              id="compareSelect 1"
              options={versions.filter((ver) => ver !== selectedVersion2)}
              title={'Version 1'}
              label=""
              value={selectedVersion1}
              onChange={(e) => setSelectedVersion1(e.target.value)}
            />
            <Dropdown
              id="compareSelect 2"
              options={versions.filter((ver) => ver !== selectedVersion1)}
              title={'Version 2'}
              label=""
              value={selectedVersion2}
              onChange={(e) => setSelectedVersion2(e.target.value)}
            />
            <div className="flex items-center gap-4">
              <button
                className="btn btn-enabled"
                onClick={handleCompare}
                disabled={!selectedVersion1 || !selectedVersion2}
              >
                Compare
              </button>
            </div>
          </div>
        </div>
      </div>

      {!hasComparison ? (
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] py-8 px-5 text-center text-gray-500">
          Select two versions and press <span className="font-semibold">Compare</span> to see a section-by-section
          breakdown of the changes.
        </div>
      ) : (
        <>
          {/* ---- Toolbar ---- */}
          <div className="diff-toolbar">
            <div className="diff-counts">
              <span className="diff-badge diff-badge-modified">{counts.modified} modified</span>
              <span className="diff-badge diff-badge-added">{counts.added} added</span>
              <span className="diff-badge diff-badge-removed">{counts.removed} removed</span>
              <span className="diff-badge diff-badge-unchanged">{counts.unchanged} unchanged</span>
            </div>
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} />
                Only changes
              </label>
              <button className="text-sm text-darkblue underline" onClick={() => setAllRows(true)}>
                Expand all
              </button>
              <button className="text-sm text-darkblue underline" onClick={() => setAllRows(false)}>
                Collapse all
              </button>
              <button className="text-sm text-darkblue underline" onClick={() => setSideBySide((v) => !v)}>
                {sideBySide ? 'View: Inline' : 'View: Side by side'}
              </button>
            </div>
          </div>

          {changedCount === 0 && (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] py-4 px-5 mb-[20px] text-gray-600">
              No substantive changes were detected between {selectedVersion1} and {selectedVersion2}. The versions are
              substantially the same.
            </div>
          )}

          {/* ---- Section rows + changes navigator ---- */}
          <div className="diff-layout">
            <div className="diff-main">
              {visibleRows.map(({ row, idx }) => {
                const open = isRowOpen(row, idx);
                const badge = BADGE[row.changeType] || BADGE.unchanged;
                const titleSource =
                  row.changeType === 'added' ? (row.diff || []).map((d) => d.text).join('') : row.left;
                return (
                  <div className={`diff-row ${open ? 'open' : ''}`} id={`diff-row-${idx}`} key={idx}>
                    <div className="diff-row-header" onClick={() => toggleRow(row, idx)}>
                      <span className={`diff-chevron ${open ? 'open' : ''}`}>▶</span>
                      <span className={`diff-badge ${badge.cls}`}>{badge.label}</span>
                      <span className="diff-row-title">{firstMeaningfulLine(titleSource)}</span>
                      {row.score != null && row.changeType === 'modified' && (
                        <span className="diff-row-score">match {(row.score * 100).toFixed(0)}%</span>
                      )}
                    </div>
                    {open && <div className="diff-row-body">{renderRowBody(row)}</div>}
                  </div>
                );
              })}
            </div>

            <aside className="diff-sidebar">
              <div className="diff-sidebar-inner">
                <div className="diff-sidebar-tabs">
                  <button
                    className={`diff-tab ${sidebarTab === 'impact' ? 'active' : ''}`}
                    onClick={() => setSidebarTab('impact')}
                  >
                    Impact
                  </button>
                  <button
                    className={`diff-tab ${sidebarTab === 'changes' ? 'active' : ''}`}
                    onClick={() => setSidebarTab('changes')}
                  >
                    Changes ({changedCount})
                  </button>
                </div>
                <div className="diff-sidebar-body">
                  {sidebarTab === 'impact' ? (
                    aiResponse ? (
                      <div className="prose max-w-none text-black text-sm leading-6 diff-impact">
                        <ReactMarkdown>{aiResponse}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="diff-sidebar-empty">Generating summary…</p>
                    )
                  ) : changedCount === 0 ? (
                    <p className="diff-sidebar-empty">No changes detected.</p>
                  ) : (
                    <ul className="diff-nav">
                      {changedRows.map(({ row, idx }) => (
                        <li key={idx}>
                          <button className="diff-nav-item" onClick={() => jumpToRow(idx)}>
                            <span className={`diff-nav-dot diff-dot-${row.changeType}`} />
                            <span className="diff-nav-label">{navLabel(row)}</span>
                            {row.score != null && row.changeType === 'modified' && (
                              <span className="diff-nav-score">{(row.score * 100).toFixed(0)}%</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </>
      )}

      <div className="insufficient">
        The responses on this website are generated by artificial intelligence tools and do not reflect the views or
        opinions of our company. Please verify information independently.
      </div>
    </>
  );
};

export default Compare;
